import React from 'react';
import { AppButton, AppCheckbox, AppSelect, AppTag, SearchTextField } from '@/components/atoms';
import { Download } from '@/components/atoms/AppIcon';
import { RA2NDatePicker } from '@/components/organisms';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { SERVICE_TYPE_R2, SERVICE_TYPE_A2, SERVICE_TYPE_LABELS } from '@/constants/serviceType';
import { Flex } from 'antd';

type RezeptHistoriesFilterPanelProps = {
  canUseSiteTypeFeatures: boolean;
  tenants: TenantSiteTypeItem[];

  siteType: string;
  onChangeSiteType: (value: string) => void;

  serviceType: string;
  onChangeServiceType: (value: string) => void;

  rezeptYearmonth: string;
  onChangeRezeptYearmonth: (value: string) => void;

  clinicNumber: string;
  onChangeClinicNumber: (value: string) => void;

  forcedOutput: boolean;
  onChangeForcedOutput: (value: boolean) => void;

  onAggregateExport: () => void;
  onDivideExport: () => void;
  isAggregateExporting: boolean;
  isDivideExporting: boolean;

  yearmonthError?: string;
};

const SERVICE_TYPE_OPTIONS = [
  { label: 'すべて', value: '' },
  { label: SERVICE_TYPE_LABELS[SERVICE_TYPE_R2], value: SERVICE_TYPE_R2 },
  { label: SERVICE_TYPE_LABELS[SERVICE_TYPE_A2], value: SERVICE_TYPE_A2 },
];

const RezeptHistoriesFilterPanel: React.FC<RezeptHistoriesFilterPanelProps> = ({
  canUseSiteTypeFeatures,
  tenants,
  siteType,
  onChangeSiteType,
  serviceType,
  onChangeServiceType,
  rezeptYearmonth,
  onChangeRezeptYearmonth,
  clinicNumber,
  onChangeClinicNumber,
  forcedOutput,
  onChangeForcedOutput,
  onAggregateExport,
  onDivideExport,
  isAggregateExporting,
  isDivideExporting,
  yearmonthError,
}) => {
  const { colors } = useTheme();

  const siteTypeOptions = tenants.map(t => ({ label: t.name, value: t.site_type }));

  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      {/* Row 1: 4 inputs equal width + checkbox */}
      <div className="flex gap-4 items-end flex-wrap mb-10">
        {canUseSiteTypeFeatures && (
          <div className="flex-1 min-w-[120px]">
            <label htmlFor="site_type" className="mb-1 block text-sm text-gray-600">
              サイト区分
            </label>
            <AppSelect
              id="site_type"
              className="w-full"
              placeholder="サイト区分"
              value={siteType || undefined}
              options={siteTypeOptions}
              onChange={(value: unknown) => onChangeSiteType((value as string) ?? '')}
            />
          </div>
        )}

        <div className="flex-1 min-w-[120px]">
          <label htmlFor="service_type" className="mb-1 block text-sm text-gray-600">
            サービス区分
          </label>
          <AppSelect
            id="service_type"
            className="w-full"
            value={serviceType || undefined}
            placeholder="すべて"
            options={SERVICE_TYPE_OPTIONS}
            onChange={(value: unknown) => onChangeServiceType((value as string) ?? '')}
            allowClear
          />
        </div>

        <div className="flex-1 min-w-[120px] relative">
          <label htmlFor="rezept_yearmonth" className="mb-1 block text-sm text-gray-600">
            提出年月
            <AppTag color="magenta">必須</AppTag>
          </label>
          <RA2NDatePicker
            picker="month"
            className="w-full"
            value={rezeptYearmonth || ''}
            onChange={v => onChangeRezeptYearmonth(v)}
            placeholder="年月指定"
            onEnter={onAggregateExport}
          />
          {yearmonthError && (
            <div className="absolute left-0 top-full mt-1 text-xs text-red-500">
              {yearmonthError}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-[120px]">
          <label htmlFor="clinic_number" className="mb-1 block text-sm text-gray-600">
            治療院ID
          </label>
          <SearchTextField
            id="clinic_number"
            value={clinicNumber}
            placeholder="カンマ区切りで複数指定可能"
            onChange={e => onChangeClinicNumber(e.target.value)}
          />
        </div>

        <div className="flex items-center self-end pb-1 shrink-0">
          <AppCheckbox
            checked={forcedOutput}
            onChange={e => onChangeForcedOutput(e.target.checked)}
          >
            出力済を含む
          </AppCheckbox>
        </div>
      </div>
      {/* Row 2: 2 buttons centered */}
      <div className="flex justify-center mt-7 gap-3">
        <AppButton
          onClick={onAggregateExport}
          type="primary"
          icon={<Download />}
          loading={isAggregateExporting}
          disabled={isDivideExporting}
          style={{
            backgroundColor: colors.primary,
            borderColor: colors.primary,
            color: '#fff',
            opacity: isDivideExporting ? 0.5 : 1,
          }}
          className="py-1 px-6 text-white btn-ra2-hover font-medium !h-[40px]"
        >
          結合CSV
        </AppButton>
        <AppButton
          onClick={onDivideExport}
          type="primary"
          icon={<Download />}
          loading={isDivideExporting}
          disabled={isAggregateExporting}
          style={{
            backgroundColor: colors.primary,
            borderColor: colors.primary,
            color: '#fff',
            opacity: isAggregateExporting ? 0.5 : 1,
          }}
          className="py-1 px-6 text-white btn-ra2-hover font-medium !h-[40px]"
        >
          分割CSV
        </AppButton>
      </div>
      <Flex className="text-amber-500 mt-6" justify="center" vertical align="center">
        <div>
          <p>結合CSV　...審査業務用</p>
          <p>分割CSV　...基幹システムインポート用</p>
        </div>
      </Flex>
    </div>
  );
};

export default RezeptHistoriesFilterPanel;
